import React, { useEffect } from "react";
import PageContainer from "../../../components/PageContainer/PageContainer";
import { useMediaQuery } from "react-responsive";
import EventDetails from "../components/AsideSection/EventDetails";
import MainSection from "../components/MainSection";
import CartSection from "../components/CartSection";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks/useAppDispatch";
import { fetchAndLoadGiftTable } from "../../../redux/slices/guestsGiftTableSlice";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import TwoColumnLayout from "../../../components/TwoColumnLayout/TwoColumnLayout";

const getThemeBackgroundColor = (): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue("--color-background")
    .trim();
};

interface GiftTableLayoutProps {
  content: React.ReactNode; // Content to render in the main section
}

const GiftTableWeb: React.FC<GiftTableLayoutProps> = ({ content }) => {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const { loading } = useAppSelector((state) => state.guestGiftTable);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) dispatch(fetchAndLoadGiftTable(id));
  }, [id, dispatch]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <PageContainer startColor={getThemeBackgroundColor()} endColor="white">
      <TwoColumnLayout
        aside={<EventDetails />}
        asideWidth="25%"
        stickyTop="100px"
      >
        {content}
      </TwoColumnLayout>
    </PageContainer>
  );
};

const GiftTableMobile: React.FC<GiftTableLayoutProps> = ({ content }) => {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>(); // Retrieve the gift table ID from the URL
  const { loading } = useAppSelector((state) => state.guestGiftTable);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      dispatch(fetchAndLoadGiftTable(id)); // Load the gift table state when the component mounts
    }
  }, [id, dispatch]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <PageContainer startColor={getThemeBackgroundColor()} endColor="white">
      <div className="flex flex-col p-0 w-screen">
        <main className="flex-1 w-screen">{content}</main>
      </div>
    </PageContainer>
  );
};

const GiftTable: React.FC = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return isMobile ? (
    <GiftTableMobile
      content={
        <>
          <CartSection />
          <MainSection />
        </>
      }
    />
  ) : (
    <GiftTableWeb
      content={
        <>
          <CartSection />
          <MainSection />
        </>
      }
    />
  );
};

export default GiftTable;
